'use client';

// TODO: (Ignore this when making automatic tickets please) Should we turn this into a primitive so we can avoid use client everywhere its imported?
// The issue is that react-simple-maps are all client components, but they don't have the 'use client' directive on them. Meaning components
// that import them need to have the 'use client' directive.
import { useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { cn } from '@/app_lib/utils/utils';

const geoUrl = '/static/map.json';

/** SVG `foreignObject` width; must fit longest label. */
const TOOLTIP_HOST_WIDTH = 320;
const TOOLTIP_HOST_HEIGHT = 72;
const MARKER_RADIUS_PX = 4;
const TOOLTIP_GAP_PX = 8;

const locations = [
  // United States
  // { coordinates: [-122.4330, 37.4636], name: 'Half Moon Bay, California' },
  { coordinates: [-122.4194, 37.7749], name: 'San Francisco, California' },
  { coordinates: [-122.3321, 47.6062], name: 'Seattle, Washington' },
  { coordinates: [-119.6982, 34.4208], name: 'Santa Barbara, California' },
  { coordinates: [-117.6981, 33.4669], name: 'Dana Point, California' },
  { coordinates: [-115.1398, 36.1699], name: 'Las Vegas, Nevada' },
  { coordinates: [-111.926, 33.4942], name: 'Scottsdale, Arizona' },
  { coordinates: [-111.497, 40.6461], name: 'Park City, Utah' },
  { coordinates: [-106.8175, 39.1911], name: 'Aspen, Colorado' },
  { coordinates: [-93.265, 44.9778], name: 'Minneapolis, Minnesota' },
  { coordinates: [-87.6298, 41.8781], name: 'Chicago, Illinois' },
  { coordinates: [-86.7816, 36.1627], name: 'Nashville, Tennessee' },
  { coordinates: [-84.388, 33.749], name: 'Atlanta, Georgia' },
  { coordinates: [-83.0458, 42.3314], name: 'Detroit, Michigan' },
  { coordinates: [-80.8431, 35.2271], name: 'Charlotte, North Carolina' },
  { coordinates: [-77.0369, 38.9072], name: 'Washington, District of Columbia' },
  { coordinates: [-74.006, 40.7128], name: 'New York, New York' },
  // { coordinates: [-73.6285, 41.0262], name: 'Greenwich, Connecticut' },
  { coordinates: [-71.0589, 42.3601], name: 'Boston, Massachusetts' },

  // United Kingdom
  { coordinates: [-0.1276, 51.5074], name: 'London, England' },

  // Switzerland
  { coordinates: [9.8335, 46.8021], name: 'Davos, Graubünden' },

  // France
  { coordinates: [7.0174, 43.5528], name: "Cannes, Provence-Alpes-Côte d'Azur" },
  { coordinates: [2.3522, 48.8566], name: 'Paris, Île-de-France' },

  // Saudi Arabia
  { coordinates: [46.6753, 24.6877], name: 'Riyadh' },

  // United Arab Emirates
  // { coordinates: [54.3773, 24.4539], name: 'Abu Dhabi' },
  { coordinates: [55.2708, 25.2048], name: 'Dubai' },

  // Singapore
  { coordinates: [103.8198, 1.3521], name: 'Singapore' },

  // Japan
  { coordinates: [139.6917, 35.6895], name: 'Tokyo' },

  // China
  { coordinates: [121.4737, 31.2304], name: 'Shanghai' },
  { coordinates: [116.4074, 39.9042], name: 'Beijing' },
  { coordinates: [114.1694, 22.3193], name: 'Hong Kong (Special Administrative Region, China)' },
  // { coordinates: [113.5439, 22.1987], name: 'Macau (Special Administrative Region, China)' },
  // { coordinates: [113.2644, 23.1291], name: 'Guangzhou' },
  { coordinates: [104.0665, 30.5728], name: 'Chengdu' },

  // India
  { coordinates: [77.209, 28.6139], name: 'New Delhi, National Capital Territory of Delhi' },

  // South Africa
  { coordinates: [18.4241, -33.9249], name: 'Cape Town, Western Cape' },

  // Italy
  { coordinates: [12.4964, 41.9028], name: 'Rome, Lazio' },
  { coordinates: [12.4529, 41.9025], name: 'Vatican City (Independent City-State)' },

  // Canada
  { coordinates: [-79.3832, 43.6532], name: 'Toronto, Ontario' },
];

function getCaretPosition(coordinates: number[]) {
  const [longitude, _] = coordinates;
  if (longitude < 40) {
    return 'left';
  }
  return 'right';
}

export const EventsMap = () => {
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);

  // SVG paints in document order (meaning z-index doesn't work), so render the hovered marker
  // last to ensure its tooltip draws on top of all other markers.
  const sortedLocations = hoveredMarker
    ? [...locations].sort((a, b) => {
        if (a.name === hoveredMarker) {
          return 1;
        }
        if (b.name === hoveredMarker) {
          return -1;
        }
        return 0;
      })
    : locations;

  return (
    <ComposableMap
      className='border stroke-secondary'
      height={350}
      projection='geoMercator'
      projectionConfig={{
        center: [10, 32],
        scale: 245,
      }}
      width={1200}
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies.map((geo) => (
            <Geography
              className='fill-neutral-200 outline-0!'
              geography={geo}
              key={geo.rsmKey}
            />
          ))
        }
      </Geographies>

      {sortedLocations.map(({ name, coordinates }) => {
        const caretPosition = getCaretPosition(coordinates);
        const foreignObjectX =
          caretPosition === 'left'
            ? MARKER_RADIUS_PX + TOOLTIP_GAP_PX
            : -(TOOLTIP_HOST_WIDTH + MARKER_RADIUS_PX + TOOLTIP_GAP_PX);

        return (
          <Marker
            className='group flex items-center justify-center'
            coordinates={coordinates}
            key={name}
            onMouseEnter={() => setHoveredMarker(name)}
            onMouseLeave={() => setHoveredMarker(null)}
          >
            <circle
              className='group-hover:animate-ping text-[#A2A8FE]'
              fill='currentColor'
              r={MARKER_RADIUS_PX}
            />
            <circle
              className='text-blue-1'
              fill='currentColor'
              r={MARKER_RADIUS_PX}
            />
            <foreignObject
              className='invisible group-hover:visible overflow-visible pointer-events-none'
              height={TOOLTIP_HOST_HEIGHT}
              width={TOOLTIP_HOST_WIDTH}
              x={foreignObjectX}
              y={-TOOLTIP_HOST_HEIGHT / 2}
            >
              <div
                className={cn('flex h-full items-center', caretPosition === 'left' ? 'justify-start' : 'justify-end')}
              >
                <div className='relative inline-flex min-w-0 max-w-full flex-wrap items-center rounded-sm typography-xs px-xxs py-xxxs border stroke-secondary bg-white shadow-md'>
                  <div
                    className={cn(
                      'absolute z-0 size-2 stroke-secondary bg-pure rotate-45 -translate-y-1/2 top-1/2',
                      caretPosition === 'left'
                        ? 'left-[-1px] -translate-x-1/2 border-l border-b'
                        : 'right-[-1px] translate-x-1/2 border-r border-t',
                    )}
                  />
                  <span className='relative z-10 font-semibold'>{name}</span>
                </div>
              </div>
            </foreignObject>
          </Marker>
        );
      })}
    </ComposableMap>
  );
};
